import axios from 'axios';

/**
 * Invoice Service
 * Handles all API calls related to invoices, payments, settings and trigger
 * availability. Mirrors the backend contract documented in INVOICING_DOCS_README.md.
 */
export const invoiceService = {
  // ============================================
  // LIST & DETAIL
  // ============================================

  /**
   * Get invoices with filters
   * @param {Object} params - { status?, invoice_type?, lead_id?, deal_id?, proposal_id?,
   *   project_id?, from_date?, to_date?, search?, page?, limit? }
   */
  async getInvoices(params = {}) {
    const response = await axios.get('/invoices', { params });
    return response.data;
  },

  async getInvoiceById(id) {
    const response = await axios.get(`/invoices/${id}`);
    return response.data;
  },

  async getStats(params = {}) {
    const response = await axios.get('/invoices/stats', { params });
    return response.data;
  },

  // ============================================
  // BILLABLE ITEMS (Sprint 3.5 — project-driven)
  // ============================================

  /**
   * Returns project_items for a project, decorated with already_billed status.
   * Used by the project-driven invoice creation modal.
   */
  async getBillableItems(projectId) {
    const response = await axios.get(`/invoices/billable-items/${projectId}`);
    return response.data;
  },

  // ============================================
  // CREATE
  // ============================================

  /**
   * Project-driven invoice creation.
   * @param {Object} data - {
   *   project_id (required),
   *   project_item_ids? (subset; default = all unbilled),
   *   additional_items? ([{ description, quantity, unit_price, vat_rate, item_type }] —
   *      item_type must be 'discount' | 'surcharge' | 'text'),
   *   client_notes?, internal_notes?, customer_reference?, due_date?, invoice_date?,
   *   currency?, prices_include_vat?
   * }
   */
  async createInvoice(data) {
    const response = await axios.post('/invoices', data);
    return response.data;
  },

  async createFromProposal(proposalId, data = {}) {
    const response = await axios.post(`/invoices/from-proposal/${proposalId}`, data);
    return response.data;
  },

  /**
   * Merge 2+ draft invoices for the same lead into a new draft.
   * @param {string[]} invoiceIds
   */
  async mergeDraftInvoices(invoiceIds) {
    const response = await axios.post('/invoices/merge', {
      invoice_ids: invoiceIds,
    });
    return response.data;
  },

  /**
   * Create deposit invoice for a project
   * @param {string} projectId
   * @param {Object} body - { amount } | { percentage } | {} (uses settings)
   */
  async createDepositInvoice(projectId, body = {}) {
    const response = await axios.post(`/invoices/deposit/${projectId}`, body);
    return response.data;
  },

  async createFinalInvoice(projectId, body = {}) {
    const response = await axios.post(`/invoices/final/${projectId}`, body);
    return response.data;
  },

  /**
   * Create credit note based on an existing invoice
   * @param {string} invoiceId
   * @param {Object} body - { reason, items? }
   */
  async createCreditNote(invoiceId, body) {
    const response = await axios.post(`/invoices/${invoiceId}/credit-note`, body);
    return response.data;
  },

  // ============================================
  // UPDATE / CANCEL
  // ============================================

  async updateInvoice(id, data) {
    const response = await axios.patch(`/invoices/${id}`, data);
    return response.data;
  },

  async deleteDraft(id) {
    const response = await axios.delete(`/invoices/${id}`);
    return response.data;
  },

  // ============================================
  // STATUS TRANSITIONS
  // ============================================

  async sendInvoice(id) {
    const response = await axios.post(`/invoices/${id}/send`);
    return response.data;
  },

  // ============================================
  // PDF & EMAIL
  // ============================================

  /**
   * Get the PDF URL with optional regenerate flag
   */
  pdfUrl(id, { regenerate = false, download = false } = {}) {
    const baseURL = axios.defaults.baseURL || '';
    const params = new URLSearchParams();
    if (regenerate) params.append('regenerate', 'true');
    if (download) params.append('download', 'true');
    const qs = params.toString();
    return `${baseURL}/invoices/${id}/pdf${qs ? `?${qs}` : ''}`;
  },

  async fetchPdfBlob(id, { regenerate = false } = {}) {
    const response = await axios.get(`/invoices/${id}/pdf`, {
      params: regenerate ? { regenerate: true } : {},
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Send the invoice via email (async).
   *
   * Backend returns `202 Accepted` with `{ invoice, job_id, queued_recipients }`.
   * The invoice will be in `status='sending'` until the worker has finished;
   * callers should poll `GET /invoices/:id` and watch for `sent` (success)
   * or `draft` (failure — worker reverted).
   *
   * For backwards-compatibility we still return `{ status, data }` so a legacy
   * 200 response (older backend) can be detected by callers if needed.
   *
   * @param {string} id
   * @param {Object} body - { to?, subject?, message?, send_to_user?, send_to_user_email? }
   * @returns {Promise<{ status: number, data: any }>}
   */
  async emailInvoice(id, body = {}) {
    const response = await axios.post(`/invoices/${id}/email`, body);
    return { status: response.status, data: response.data };
  },

  /**
   * What the customer receives for this send (headers incl. BCC, rendered
   * email, attachments). Same body as emailInvoice; nothing is sent.
   * @param {string} id
   * @param {Object} body - { to?, subject?, message? }
   */
  async previewEmailInvoice(id, body = {}) {
    const response = await axios.post(`/invoices/${id}/email-preview`, body);
    return response.data;
  },

  /**
   * Manually send a payment reminder for an open invoice.
   * @param {string} id
   * @param {Object} body - { to?, subject?, message? }
   */
  async remindInvoice(id, body = {}) {
    const response = await axios.post(`/invoices/${id}/remind`, body);
    return response.data;
  },

  /**
   * Bulk send payment reminders.
   * @param {string[]} invoiceIds
   */
  async remindInvoicesBulk(invoiceIds) {
    const response = await axios.post('/invoices/remind', { invoice_ids: invoiceIds });
    return response.data;
  },

  // ============================================
  // PAYMENTS
  // ============================================

  async getPayments(id) {
    const response = await axios.get(`/invoices/${id}/payments`);
    return response.data;
  },

  /**
   * Record a payment
   * @param {string} id
   * @param {Object} body - { amount, payment_date, payment_method, reference?, notes? }
   */
  async recordPayment(id, body) {
    const response = await axios.post(`/invoices/${id}/payments`, body);
    return response.data;
  },

  async deletePayment(id, paymentId) {
    const response = await axios.delete(`/invoices/${id}/payments/${paymentId}`);
    return response.data;
  },

  // ============================================
  // PROJECT SUMMARY
  // ============================================

  async getProjectSummary(projectId) {
    const response = await axios.get(`/invoices/project-summary/${projectId}`);
    return response.data;
  },

  async writeOffProjectRemainder(projectId, body = {}) {
    const response = await axios.post(`/invoices/project-write-off/${projectId}`, body);
    return response.data;
  },

  // ============================================
  // PUBLIC (no auth)
  // ============================================

  async getPublicInvoice(token) {
    const response = await axios.get(`/invoices/public/${token}`);
    return response.data;
  },

  // ============================================
  // SETTINGS
  // ============================================

  async getSettings() {
    const response = await axios.get('/invoices/settings');
    return response.data;
  },

  async updateSettings(data) {
    const response = await axios.patch('/invoices/settings', data);
    return response.data;
  },

  // ============================================
  // TRIGGER AVAILABILITY
  // ============================================

  async getTriggerAvailability() {
    const response = await axios.get('/invoices/trigger-availability');
    return response.data;
  },
};

export default invoiceService;
